import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, switchMap } from 'rxjs';
import { Floor } from '../models/floor.interface';
import { SavedEntity } from '../models/saved-entity.interface';
import { FloorResource } from '../resources/floor.resource';
import { catchError, concatMap, filter, map, shareReplay, startWith } from 'rxjs/operators';
import { ActivatedRouteSnapshot, NavigationEnd, Params, Router } from '@angular/router';
import { UserService } from '@services/user/user.service';
import { ToastService } from '@services/toast/toast.service';
import { ImageDimensions } from '@components/floorplan/floorplan.component';

@Injectable({
  providedIn: 'root'
})
export class FloorService {
  private static MAX_DIMENSION_ON_SMALL_SCREENS = 1000;
  private floorCache: Record<number, Observable<Floor>> = {};
  private currentFloorId$ = new BehaviorSubject<number>(null);

  constructor(
    private readonly floorResource: FloorResource,
    private readonly router: Router,
    private readonly userService: UserService,
    private toast: ToastService
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(this.router),
        map((_) => this.extractParams(this.router.routerState.snapshot.root)),
        filter((params) => params.buildingId && params.floorId),
        switchMap((params) =>
          forkJoin([
            of(params),
            this.userService.getFloorsForBuilding(params.buildingId).pipe(catchError((_) => of(null)))
          ])
        ),
        map(([params, floors]) => ({ floors, params }))
      )
      .subscribe(({ floors, params }) => {
        const { floorId, buildingId } = params;
        // if floors is received as null, it means an invalid buildingId was passed in the url
        // if there were no floors for the building, then also we should navigate to home
        if (floors == null || floors.length === 0) {
          this.showErrorAndNavigateToHome(`Could not find floors for building with id ${buildingId}`);
          this.currentFloorId$.next(null);
          return;
        }
        const isFloorExisting = floors.some((f) => f.id === Number(floorId));
        if (!isFloorExisting) {
          // choose the first floor in the list if the floorId is invalid
          this.currentFloorId$.next(Number(floors[0].id));
          return;
        }
        // if all is good lets update the current floor id, check if the floorId is different from the currentFloorId$
        if (Number(floorId) !== this.currentFloorId$.getValue()) {
          this.currentFloorId$.next(Number(floorId));
        }
      });
  }

  private extractParams(route: ActivatedRouteSnapshot): Params {
    const params: Params = {};
    // Traverse the route tree to extract the params
    while (route) {
      Object.assign(params, route.params);
      route = route.firstChild;
    }
    return params;
  }

  private showErrorAndNavigateToHome(message: string): void {
    this.toast.error({
      message,
      dataCy: 'load-error-toast',
      autoClose: false,
      dismissible: true
    });
    this.router.navigate(['/buildings']).catch((err) => console.error(err));
  }

  private retrieveFloor(floorId: number): Observable<Floor> {
    if (!this.floorCache[floorId]) {
      this.floorCache[floorId] = this.floorResource.retrieve(floorId).pipe(shareReplay());
    }
    return this.floorCache[floorId];
  }

  get currentFloorIdValue(): number {
    return this.currentFloorId$.getValue();
  }

  getCurrentFloorId(): Observable<number> {
    return this.currentFloorId$.asObservable();
  }

  getCurrentFloor(): Observable<Floor> {
    // currentFloorId$ subject starts with null. we return "null" wrapped as an Observable in that case
    return this.currentFloorId$.pipe(concatMap((floorId) => (floorId ? this.retrieveFloor(floorId) : of(null))));
  }

  getAllFloorsForBuildingId(buildingId: number): Observable<Floor[]> {
    return this.floorResource.getFloorsForBuilding(buildingId);
  }

  getFloorDimensions(floorId: number): Observable<ImageDimensions> {
    return this.floorResource.getFloorDimensions(floorId);
  }

  getFloorImageUrl(floor: Floor, width = 0): string {
    return this.floorResource.getFloorImageUrl(floor, width);
  }

  calculateFloorplanScalingFactor(floor: Floor): Observable<{ scalingFactor: number; dimensions: ImageDimensions }> {
    return this.getFloorDimensions(floor.id).pipe(
      switchMap((dimensions) => {
        let scalingFactor = 1;
        if (dimensions?.width !== 0) {
          const maxDim = Math.max(dimensions.width, dimensions.height);
          if (maxDim > FloorService.MAX_DIMENSION_ON_SMALL_SCREENS) {
            scalingFactor = FloorService.MAX_DIMENSION_ON_SMALL_SCREENS / maxDim;
          }
        }
        return of({ scalingFactor, dimensions });
      }),
      catchError(() => of({ scalingFactor: 1, dimensions: { width: 0, height: 0 } })) // Return an empty string in case of error
    );
  }

  isSmallDevice(): boolean {
    const userAgent = navigator.userAgent;
    return /iPad/.test(userAgent) || /Mobile/.test(userAgent) || /Android/.test(userAgent);
  }

  invalidateFloorCache(id: number): void {
    delete this.floorCache[id];
  }

  updateFloor(floor: Floor): Observable<{}> {
    this.invalidateFloorCache(floor.id);
    return this.floorResource.update(floor.id, floor);
  }

  addFloor(floor: Floor): Observable<SavedEntity<Floor, number>> {
    return this.floorResource.add(floor);
  }

  deleteFloor(floorId: number): Observable<{}> {
    this.invalidateFloorCache(floorId);
    return this.floorResource.delete(floorId);
  }
}
